<script lang="ts">
import type { VariantProps } from 'tailwind-variants';
import type { SelectionMode } from '#layers/listing/composables/use-selection';
import { tv } from 'tailwind-variants';

const tile = tv({
	slots: {
		root: 'group flex cursor-pointer flex-col gap-0.5 rounded-sm transition-all focus-visible:outline-none',
		title: 'h-auto! truncate text-sm font-medium text-default',
		description: 'h-auto! truncate text-xs font-medium text-dimmed',
		imageWrapper: `
			relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-md border border-transparent
			bg-muted ring-0 transition-all
			group-hover:border-default
			group-focus-visible:border-none group-focus-visible:ring-3 group-focus-visible:ring-primary
			after:absolute after:inset-x-0 after:top-0 after:z-0 after:h-10 after:bg-linear-to-b
			after:from-(--ui-color-neutral-900)/20 after:to-transparent after:opacity-0 after:transition-opacity
			group-focus-within:after:opacity-100
			group-hover:after:opacity-100
			dark:bg-elevated
		`,
		image: 'aspect-square size-full object-cover',
		selector: `
			absolute top-1.5 left-1.5 z-10 inline-flex size-5 cursor-pointer items-center justify-center rounded-full border-2
			border-white text-white opacity-0 transition
			group-focus-within:opacity-100
			group-hover:opacity-100
			group-focus-visible:opacity-100
			focus:outline-none
			focus-visible:ring-2 focus-visible:ring-primary
		`,
	},
	variants: {
		selected: {
			true: {
				imageWrapper: 'border-none ring-2 ring-primary',
				selector: 'border-primary bg-primary',
			},
			false: {
				selector: 'bg-transparent',
			},
		},
		hasSelection: {
			true: {
				imageWrapper: 'after:opacity-100',
				selector: 'opacity-100',
			},
		},
		disabled: {
			true: {
				root: 'pointer-events-none cursor-not-allowed opacity-50',
			},
		},
		size: {
			sm: {
				title: 'text-xs',
			},
			md: {},
			lg: {},
		},
	},
});

type TileVariants = VariantProps<typeof tile>;
</script>

<script setup lang="ts">
import type { Component } from 'vue';
import type { AnyItem, DisplayTemplateAst, GlobalCollectionId, MonospaceFile } from '#layers/data-model/public';
import { computed } from 'vue';
import { RenderAssetImage, useFileInfo } from '#layers/assets/public';
import { isEmptyDisplayTemplate } from '#layers/data-model/public';

const props = defineProps<{
	item: AnyItem;
	titleTemplate?: DisplayTemplateAst | null;
	descriptionTemplate?: DisplayTemplateAst | null;
	collectionId?: GlobalCollectionId;
	file?: MonospaceFile;
	selected?: boolean;
	disabled?: boolean;
	size: NonNullable<TileVariants['size']>;
	isSelectionEmpty?: boolean;
	selectionMode?: SelectionMode;
	focused?: boolean;
	renderDisplayTemplate: Component;
}>();

const emit = defineEmits<{
	click: [];
	select: [];
	focus: [];
}>();

const ui = computed(() =>
	tile({
		selected: props.selected,
		hasSelection: !props.isSelectionEmpty && props.selectionMode !== 'single',
		disabled: props.disabled,
		size: props.size,
	}),
);

const { isImage } = useFileInfo(() => props.file);

function handleClick() {
	if (!props.disabled) {
		emit('click');
	}
}
</script>

<template>
	<div
		role="button"
		:class="ui.root()"
		:tabindex="disabled || !focused ? -1 : 0"
		@click="handleClick"
		@focus="emit('focus')"
	>
		<div :class="ui.imageWrapper()">
			<RenderAssetImage v-if="isImage && file" :class="ui.image()" :file>
				<template #error>
					<FileTypeIcon class="size-1/2" :file />
				</template>
			</RenderAssetImage>
			<FileTypeIcon v-else-if="file" class="size-1/2" :file />
			<DIcon
				v-else
				name="lucide:image"
				class="size-1/2 text-dimmed"
				mode="svg"
			/>
			<button
				:tabindex="-1"
				:class="ui.selector()"
				:data-selected="String(selected)"
				@click.stop.prevent="$emit('select')"
			>
				<DIcon v-if="selected" name="lucide:check" class="size-5" />
			</button>
		</div>
		<div class="flex flex-col text-left">
			<component
				:is="renderDisplayTemplate"
				v-if="!isEmptyDisplayTemplate(titleTemplate)"
				:item
				:display-template="titleTemplate"
				:collection-id
				:root="false"
				:class="ui.title()"
			/>
			<component
				:is="renderDisplayTemplate"
				v-if="!isEmptyDisplayTemplate(descriptionTemplate)"
				:item
				:display-template="descriptionTemplate"
				:collection-id
				:root="false"
				:class="ui.description()"
			/>
		</div>
	</div>
</template>
